import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Target as TargetIcon } from "lucide-react";

const EMPTY = { name: "", target_type: "national", organization_id: null, location_id: null, target_conversations: 10000, start_date: new Date().toISOString().slice(0,10), end_date: new Date(Date.now()+90*864e5).toISOString().slice(0,10), active: true };

export default function AdminTargets() {
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [locs, setLocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/targets", { params: { active_only: false } }).then(r => setItems(r.data));
  useEffect(() => { load(); api.get("/organizations").then(r => setOrgs(r.data)); api.get("/locations").then(r => setLocs(r.data)); }, []);

  const save = async () => {
    try {
      const payload = { ...editing, target_conversations: parseInt(editing.target_conversations) };
      if (payload.target_type !== "organization") payload.organization_id = null;
      if (payload.target_type !== "location") payload.location_id = null;
      if (editing.id) await api.patch(`/admin/targets/${editing.id}`, payload);
      else await api.post("/admin/targets", payload);
      toast.success("Sparat"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fel"); }
  };

  return (
    <AdminLayout title="Kampanjmål" actions={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button data-testid="target-new" onClick={() => setEditing({ ...EMPTY })} className="bg-[#E1251B] hover:bg-[#C81D14] rounded-full"><Plus size={16} className="mr-1"/>Nytt mål</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Redigera" : "Nytt"} kampanjmål</DialogTitle></DialogHeader>
          {editing && (<div className="space-y-3">
            <div><Label>Namn</Label><Input data-testid="target-name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}/></div>
            <div><Label>Typ</Label>
              <Select value={editing.target_type} onValueChange={v => setEditing({ ...editing, target_type: v })}>
                <SelectTrigger data-testid="target-type"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="national">Nationellt (Skåne)</SelectItem><SelectItem value="organization">Arbetarekommun</SelectItem><SelectItem value="location">Plats</SelectItem></SelectContent>
              </Select>
            </div>
            {editing.target_type === "organization" && (
              <div><Label>Arbetarekommun</Label>
                <Select value={editing.organization_id || ""} onValueChange={v => setEditing({ ...editing, organization_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Välj"/></SelectTrigger>
                  <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>)}
            {editing.target_type === "location" && (
              <div><Label>Plats</Label>
                <Select value={editing.location_id || ""} onValueChange={v => setEditing({ ...editing, location_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Välj"/></SelectTrigger>
                  <SelectContent>{locs.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>)}
            <div><Label>Mål (antal samtal)</Label><Input data-testid="target-count" type="number" value={editing.target_conversations} onChange={e => setEditing({ ...editing, target_conversations: e.target.value })}/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Från</Label><Input data-testid="target-start" type="date" value={editing.start_date} onChange={e => setEditing({ ...editing, start_date: e.target.value })}/></div>
              <div><Label>Till</Label><Input data-testid="target-end" type="date" value={editing.end_date} onChange={e => setEditing({ ...editing, end_date: e.target.value })}/></div>
            </div>
          </div>)}
          <DialogFooter><Button data-testid="target-save" onClick={save} className="bg-[#E1251B] hover:bg-[#C81D14]">Spara</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Namn</TableHead><TableHead>Typ</TableHead><TableHead>Period</TableHead>
            <TableHead className="text-right">Framsteg</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(t => {
              const pct = Math.min(100, Math.round((t.current/t.target_conversations)*100));
              return (
                <TableRow key={t.id} data-testid={`target-row-${t.name}`}>
                  <TableCell className="font-medium flex items-center gap-2"><TargetIcon size={14} className="text-[#E1251B]"/>{t.name}</TableCell>
                  <TableCell className="capitalize">{t.target_type}</TableCell>
                  <TableCell className="text-xs text-slate-500">{t.start_date} → {t.end_date}</TableCell>
                  <TableCell className="text-right">
                    <div className="font-heading font-bold number-tabular">{t.current?.toLocaleString("sv-SE")} / {t.target_conversations.toLocaleString("sv-SE")}</div>
                    <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden w-32 ml-auto"><div className="h-full bg-[#E1251B]" style={{ width: `${pct}%` }}/></div>
                  </TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => { setEditing({ ...t }); setOpen(true); }}><Pencil size={14}/></Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </AdminLayout>
  );
}
